import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Form, Spinner, Alert, Badge } from 'react-bootstrap';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useCheckout } from '../context/CheckoutContext';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './EmbroideryDesignDetail.css';

const EmbroideryDesignDetail = () => {
  const { designId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { proceedToCheckout } = useCheckout();
  
  const [design, setDesign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mainImage, setMainImage] = useState('');
  const [selectedMachines, setSelectedMachines] = useState([]);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    fetchDesignDetails();
  }, [designId]);

  const fetchDesignDetails = async () => {
    try {
      const response = await axios.get(`https://madhusewingmachines.com/api/embroidery-designs/${designId}`);
      if (response.data.success) {
        setDesign(response.data.data);
        if (response.data.data.images && response.data.data.images.length > 0) {
          setMainImage(response.data.data.images[0].url);
        }
      }
    } catch (error) {
      console.error('Error fetching design:', error);
      setError('Failed to load design details');
    } finally {
      setLoading(false);
    }
  };

  const handleThumbnailClick = (imageUrl) => {
    setMainImage(imageUrl);
  };

  const handleMachineToggle = (machineName) => {
    setSelectedMachines(prev => {
      if (prev.includes(machineName)) {
        return prev.filter(m => m !== machineName);
      } else {
        return [...prev, machineName];
      }
    });
  };

  const handleAddToCart = () => {
    if (selectedMachines.length === 0) {
      toast.error('Please select at least one machine format');
      return;
    }

    const selectedMachineDetails = design.compatibleMachines.filter(m => selectedMachines.includes(m.name));
    const machinePrice = selectedMachineDetails.reduce((sum, m) => sum + m.price, 0);
    
    const cartItem = {
      ...design,
      selectedMachines: selectedMachineDetails,
      quantity,
      type: 'embroidery-design',
      price: machinePrice, // Price is only from machines
      totalPrice: machinePrice * quantity
    };

    addToCart(cartItem);
    toast.success('Design added to cart successfully!');
  };

  const handleBuyNow = () => {
    if (selectedMachines.length === 0) {
      toast.error('Please select at least one machine format');
      return;
    }

    const selectedMachineDetails = design.compatibleMachines.filter(m => selectedMachines.includes(m.name));
    const machinePrice = selectedMachineDetails.reduce((sum, m) => sum + m.price, 0);
    
    const cartItem = {
      ...design,
      _id: design._id, // Keep _id for proceedToCheckout to convert to productId
      selectedMachines: selectedMachineDetails,
      quantity,
      type: 'embroidery-design',
      price: machinePrice, // Price is only from machines
      totalPrice: machinePrice * quantity
    };

    // Use proceedToCheckout to properly set cart items and calculate totals
    proceedToCheckout([cartItem]);
    navigate('/checkout');
  };

  if (loading) {
    return (
      <Container className="text-center py-5" style={{ marginTop: '100px' }}>
        <Spinner animation="border" />
        <p className="mt-3">Loading design details...</p>
      </Container>
    );
  }

  if (error || !design) {
    return (
      <Container style={{ marginTop: '100px' }}>
        <Alert variant="danger">{error || 'Design not found'}</Alert>
        <Button variant="primary" onClick={() => navigate('/embroidery-designs')}>
          Back to Designs
        </Button>
      </Container>
    );
  }

  const totalPrice = selectedMachines.length > 0
    ? selectedMachines.reduce((sum, machineName) => {
        const machine = design.compatibleMachines.find(m => m.name === machineName);
        return sum + (machine?.price || 0);
      }, 0) * quantity
    : 0;

  return (
    <Container className="design-detail-container" style={{ marginTop: '100px', marginBottom: '50px' }}>
      <Row>
        {/* Left Section - Images */}
        <Col xs={12} md={6} className="mb-4">
          <div className="main-image-container shadow-sm rounded overflow-hidden mb-3">
            <Card className="border-0">
              <Card.Img
                src={`https://madhusewingmachines.com/uploads/${mainImage}`}
                alt={design.name}
                className="img-fluid"
                style={{ backgroundColor: '#000', objectFit: 'contain', height: '400px' }}
              />
            </Card>
          </div>
          
          {design.images && design.images.length > 1 && (
            <Row className="g-2">
              {design.images.map((img, index) => (
                <Col key={index} xs={3}>
                  <img
                    src={`https://madhusewingmachines.com/uploads/${img.url}`}
                    alt={`${design.name} view ${index + 1}`}
                    className={`img-thumbnail cursor-pointer ${mainImage === img.url ? 'border-primary' : ''}`}
                    onClick={() => handleThumbnailClick(img.url)}
                    style={{ cursor: 'pointer', height: '80px', objectFit: 'contain', backgroundColor: '#000' }}
                  />
                </Col>
              ))}
            </Row>
          )}
        </Col>

        {/* Right Section - Details */}
        <Col xs={12} md={6}>
          <h2 className="mb-2">{design.designCode}</h2>
          <p className="text-muted">{design.name}</p>

          {/* Dimensions */}
          {design.dimensions && (
            <div className="mb-3">
              <h6 className="fw-bold">Dimensions</h6>
              <p className="mb-1">Height: {design.dimensions.height}</p>
              <p className="mb-0">Width: {design.dimensions.width}</p>
            </div>
          )}

          {/* Stitch Details */}
          {design.stitchDetails && (
            <div className="mb-3">
              <h6 className="fw-bold">Stitch Details</h6>
              <p className="mb-0">Stitch Count: {design.stitchDetails.count}</p>
              {design.stitchDetails.speed && (
                <p className="mb-0">Speed: {design.stitchDetails.speed}</p>
              )}
            </div>
          )}

          {/* Price */}
          <div className="mb-3">
            {selectedMachines.length > 0 ? (
              <>
                <span className="h3 fw-bold text-success me-2">₹{totalPrice}</span>
                {design.discount && (
                  <Badge bg="success">{design.discount}</Badge>
                )}
              </>
            ) : (
              <div className="text-muted">
                <small>Select machine(s) to see price</small>
              </div>
            )}
          </div>

          {/* Machine Selection */}
          <div className="mb-4">
            <h6 className="fw-bold">Select Machine(s)</h6>
            <p className="text-muted small mb-2">
              Selected: {selectedMachines.length} machine(s) | Total: ₹{totalPrice}
            </p>
            {design.compatibleMachines && design.compatibleMachines.length > 0 ? (
              <Form>
                {design.compatibleMachines.map((machine, index) => (
                  <Form.Check
                    key={index}
                    type="checkbox"
                    id={`machine-${index}`}
                    name="machines"
                    label={`${machine.name} (₹${machine.price})`}
                    value={machine.name}
                    checked={selectedMachines.includes(machine.name)}
                    onChange={(e) => handleMachineToggle(machine.name)}
                    className="mb-2"
                  />
                ))}
              </Form>
            ) : (
              <Alert variant="warning">No compatible machines available for this design.</Alert>
            )}
            {selectedMachines.length > 0 && (
              <div className="mt-2">
                <small className="text-success">
                  ✓ You selected {selectedMachines.length} machine format{selectedMachines.length > 1 ? 's' : ''}
                </small>
              </div>
            )}
          </div>

          {/* Quantity */}
          <div className="d-flex align-items-center mb-4">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
            >
              −
            </Button>
            <span className="mx-3 h5 mb-0">{quantity}</span>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setQuantity(quantity + 1)}
            >
              +
            </Button>
          </div>

          {/* Action Buttons */}
          <div className="d-flex gap-2 mb-4">
            <Button
              variant="success"
              className="w-100"
              onClick={handleBuyNow}
            >
              Buy Now
            </Button>
          </div>

          {/* Description */}
          {design.description && (
            <div className="mt-4">
              <h6 className="fw-bold">Product Description</h6>
              <p className="text-muted">{design.description}</p>
            </div>
          )}

          {/* Tags */}
          {design.tags && design.tags.length > 0 && (
            <div className="mt-3">
              <h6 className="fw-bold">Tags:</h6>
              {design.tags.map((tag, index) => (
                <Badge key={index} bg="secondary" className="me-2">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </Col>
      </Row>

      <ToastContainer position="top-center" autoClose={2000} />
    </Container>
  );
};

export default EmbroideryDesignDetail;
